import { api } from './client';
import {
  apiKeyViewSchema,
  issuedKeySchema,
  pageSchema,
  partnerDetailSchema,
  partnerRefSchema,
  partnerKeysSchema,
  partnerViewSchema,
  type ApiKeyView,
  type CreatePartnerRequest,
  type IssueKeyRequest,
  type IssuedKey,
  type OperatingMode,
  type Page,
  type PartnerDetail,
  type PartnerKeys,
  type PartnerRef,
  type PartnerStatus,
  type PartnerView,
} from './types';

export interface ListPartnersParams {
  q?: string;
  status?: PartnerStatus;
  page?: number;
}

const partnerPageSchema = pageSchema(partnerViewSchema);

export async function listPartners(params: ListPartnersParams = {}): Promise<Page<PartnerView>> {
  const body = await api.get<unknown>('/partners', {
    query: { q: params.q, status: params.status, page: params.page },
  });
  return partnerPageSchema.parse(body);
}

export async function fetchPartner(partnerId: string): Promise<PartnerDetail> {
  const body = await api.get<unknown>(`/partners/${partnerId}`);
  return partnerDetailSchema.parse(body);
}

export async function createPartner(request: CreatePartnerRequest): Promise<PartnerView> {
  const body = await api.post<unknown>('/partners', request);
  return partnerViewSchema.parse(body);
}

export async function setPartnerStatus(
  partnerId: string,
  status: PartnerStatus,
): Promise<PartnerView> {
  const body = await api.post<unknown>(`/partners/${partnerId}/status`, { status });
  return partnerViewSchema.parse(body);
}

export async function setPartnerOperatingMode(
  partnerId: string,
  mode: OperatingMode,
): Promise<PartnerView> {
  const body = await api.post<unknown>(`/partners/${partnerId}/operating-mode`, { mode });
  return partnerViewSchema.parse(body);
}

export async function fetchPartnerKeys(partnerId: string): Promise<PartnerKeys> {
  const body = await api.get<unknown>(`/partners/${partnerId}/keys`);
  return partnerKeysSchema.parse(body);
}

export async function issuePartnerKey(
  partnerId: string,
  request: IssueKeyRequest,
): Promise<IssuedKey> {
  const body = await api.post<unknown>(`/partners/${partnerId}/keys`, request);
  return issuedKeySchema.parse(body);
}

export async function revokePartnerKey(partnerId: string, keyId: string): Promise<ApiKeyView> {
  const body = await api.post<unknown>(`/partners/${partnerId}/keys/${keyId}/revoke`);
  return apiKeyViewSchema.parse(body);
}

export async function setPartnerKeyPermissions(
  partnerId: string,
  keyId: string,
  permissions: string[],
): Promise<ApiKeyView> {
  const body = await api.post<unknown>(`/partners/${partnerId}/keys/${keyId}/permissions`, {
    permissions,
  });
  return apiKeyViewSchema.parse(body);
}

export async function partnerOptions(): Promise<PartnerRef[]> {
  return partnerRefSchema.array().parse(await api.get<unknown>('/partners/options'));
}
